import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { ApiError } from '@/lib/api'
import { formatMoney } from '@/format/money'
import { currentLocale } from '@/i18n'
import {
  fetchAgeing,
  fetchReceivable,
  fetchSubscriptionsOf,
  type AgeingLine,
  type Invoice,
  type Receivable,
  type Subscription,
} from './invoiceApi'
import type { CardTab, CardTabContext } from './types'

/**
 * Дебиторка контрагента: ageing, открытые счета и подписки (тикет #72, ТЗ §4.3).
 *
 * ## Почему это вкладка контрагента, а не отдельный раздел
 *
 * Вопрос «сколько нам должен этот клиент и насколько просрочено» задают, глядя на клиента,
 * а не на реестр счетов. Отдельный экран дебиторки заставил бы искать того же клиента
 * второй раз — и отвечал бы той же цифрой, что вкладка «Финансы» карточки контрагента,
 * заведя второй ответ на денежный вопрос.
 *
 * ## Что здесь главное
 *
 * **Колонка «срок не наступил» отделена от просрочки.** Счёт, выставленный вчера со сроком
 * через месяц, — не долговая проблема, и включить его в просрочку значит показать её хуже,
 * чем она есть. Разделение приходит с сервера (`v_receivables_ageing.not_yet_due`), здесь
 * оно только не склеивается обратно.
 */

/** Деньги с двумя знаками: ageing читают в копейках. */
function money(value: number, currency: string | null): string {
  // One formatter for every screen (#91); the panel has no hook here, so the stored language.
  return formatMoney(value, currency, currentLocale())
}

/**
 * Ageing строкой на валюту.
 *
 * Итога «по всем валютам» здесь нет и быть не может: он потребовал бы курса, которого у
 * системы нет, — сложить рубли с евро значит выдумать его. Та же причина, по которой
 * `customer_balance` ключуется валютой.
 */
export function AgeingTable({ lines }: { lines: AgeingLine[] }) {
  const { t } = useApp()

  if (lines.length === 0) return <p className="muted">{t('card.inv.ageing.none')}</p>

  return (
    <table className="card-table">
      <thead>
        <tr>
          <th>{t('column.currency')}</th>
          {/* «Срок не наступил» стоит ПЕРВЫМ и отделён от остальных: это не нулевая
              корзина просрочки, а другая категория. Поставив её в ряд с «до 30 дней»,
              мы бы предложили читателю сложить их — что и есть ошибка. */}
          <th>{t('card.inv.ageing.notYetDue')}</th>
          <th>{t('card.inv.ageing.d30')}</th>
          <th>{t('card.inv.ageing.d60')}</th>
          <th>{t('card.inv.ageing.d90')}</th>
          <th>{t('card.inv.ageing.d90plus')}</th>
          <th>{t('card.inv.ageing.overdue')}</th>
          <th>{t('card.inv.ageing.total')}</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => {
          // Просрочено и всего — две разные цифры, и обе нужны: первая отвечает «что
          // горит», вторая «сколько должны». Одна вместо двух теряет тот вопрос,
          // который задавали.
          const overdue =
            line.upTo30Days + line.upTo60Days + line.upTo90Days + line.beyond90Days
          return (
            <tr key={line.currency}>
              <td>{line.currency}</td>
              <td>{money(line.current, line.currency)}</td>
              <td>{money(line.upTo30Days, line.currency)}</td>
              <td>{money(line.upTo60Days, line.currency)}</td>
              <td>{money(line.upTo90Days, line.currency)}</td>
              <td>{money(line.beyond90Days, line.currency)}</td>
              <td className={overdue > 0 ? 'overdue' : undefined}>
                <strong>{money(overdue, line.currency)}</strong>
              </td>
              <td>{money(line.current + overdue, line.currency)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

/** Открытые счета, старые первыми: очередь работы, а не реестр. */
function OutstandingTable({ invoices }: { invoices: Invoice[] }) {
  const { t } = useApp()

  if (invoices.length === 0) return <p className="muted">{t('card.inv.outstanding.none')}</p>

  return (
    <table className="card-table">
      <thead>
        <tr>
          <th>{t('column.number')}</th>
          <th>{t('column.issued_on')}</th>
          <th>{t('column.due_on')}</th>
          <th>{t('card.inv.total.total')}</th>
          <th>{t('card.inv.paid')}</th>
          <th>{t('card.inv.outstanding')}</th>
          <th>{t('column.status')}</th>
        </tr>
      </thead>
      <tbody>
        {invoices.map((invoice) => (
          <tr key={invoice.id}>
            <td>
              {/* Ссылка на карточку: дебиторка — это список работы, и из неё уходят
                  разбираться с конкретным счётом. */}
              <Link to={`/invoices/${invoice.id}`}>{invoice.number}</Link>
            </td>
            <td>{invoice.issuedOn}</td>
            <td>{invoice.dueOn ?? '—'}</td>
            <td>{money(invoice.total, invoice.currency)}</td>
            <td>{money(invoice.paidTotal, invoice.currency)}</td>
            <td>
              <strong>{money(invoice.outstanding, invoice.currency)}</strong>
            </td>
            <td>{t(`invoice.status.${invoice.status}`)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Подписки: расписание, ближайшее списание, приостановка (ТЗ §4.3).
 *
 * `nextRunOn` показывается **как есть**, а не пересчитывается здесь: следующий срок
 * считает домен от «сейчас», и он же хранит исходный день месяца, а не подрезанный под
 * короткий февраль. Повторив расчёт на клиенте, мы бы однажды показали 28-е там, где
 * система выставит 31-е.
 *
 * <b>Только чтение.</b> Кнопок «приостановить» здесь нет намеренно: посреди таблицы
 * дебиторки она останавливала бы выставление счетов из экрана, который открывают, чтобы
 * посмотреть долг. Название подписки ведёт в её карточку (#94, `/subscriptions/:id`), и
 * остановка с возобновлением — там.
 */
function SubscriptionsTable({ subscriptions }: { subscriptions: Subscription[] }) {
  const { t } = useApp()

  if (subscriptions.length === 0) return <p className="muted">{t('card.inv.subs.none')}</p>

  return (
    <table className="card-table">
      <thead>
        <tr>
          <th>{t('card.inv.sub.name')}</th>
          <th>{t('card.inv.sub.frequency')}</th>
          <th>{t('card.inv.sub.dayOfMonth')}</th>
          <th>{t('card.inv.sub.nextRun')}</th>
          <th>{t('column.amount')}</th>
          <th>{t('column.status')}</th>
        </tr>
      </thead>
      <tbody>
        {subscriptions.map((subscription) => (
          <tr key={subscription.id}>
            <td>
              <Link to={`/subscriptions/${subscription.id}`}>{subscription.name}</Link>
            </td>
            <td>{t(`recurring.frequency.${subscription.frequency}`)}</td>
            <td>{subscription.dayOfMonth}</td>
            <td>
              {/* Приостановленная подписка ближайшего списания не имеет — и показать
                  здесь дату значило бы обещать счёт, который не выставится. */}
              {subscription.active ? (subscription.nextRunOn ?? '—') : t('card.inv.sub.paused')}
            </td>
            <td>{subscription.amount === null ? '—' : money(subscription.amount, subscription.currency)}</td>
            <td>
              {subscription.active ? t('card.inv.sub.active') : t('card.inv.sub.inactive')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Вкладка «Дебиторка» карточки контрагента.
 *
 * Три запроса, а не один: ageing, открытые счета и подписки отвечают на разные вопросы и
 * приходят из разных эндпоинтов. Свести их в один ответ значило бы завести на сервере
 * агрегат, существующий ради одного экрана.
 */
function ReceivablesPanel({ recordId }: { recordId: string }) {
  const { t } = useApp()

  const ageing = useQuery({
    queryKey: ['counterparty-ageing', recordId],
    queryFn: ({ signal }) => fetchAgeing(recordId, signal),
    retry: false,
  })

  const receivable = useQuery({
    queryKey: ['counterparty-receivable', recordId],
    queryFn: ({ signal }) => fetchReceivable(recordId, signal),
    retry: false,
  })

  const subscriptions = useQuery({
    queryKey: ['counterparty-subscriptions', recordId],
    queryFn: ({ signal }) => fetchSubscriptionsOf(recordId, signal),
    retry: false,
  })

  const failed = ageing.error ?? receivable.error ?? subscriptions.error
  if (failed) {
    return (
      <p role="alert">
        {failed instanceof ApiError && failed.isForbidden
          ? t('access.denied')
          : t('app.error')}
      </p>
    )
  }

  if (ageing.isLoading || receivable.isLoading || subscriptions.isLoading) {
    return <p>{t('app.loading')}</p>
  }

  const debt: Receivable | undefined = receivable.data

  return (
    <div className="card-gap">
      <h3>{t('card.inv.ageing.title')}</h3>
      {/* Пояснение стоит рядом с таблицей, а не в справке: разница между «срок не
          наступил» и «просрочено» — это то, из-за чего цифру читают неправильно. */}
      <p className="muted">{t('card.inv.ageing.explain')}</p>
      <AgeingTable lines={ageing.data ?? []} />

      <h3>{t('card.inv.outstanding.title')}</h3>
      {debt && <p>{t('card.inv.outstanding.sum', { amount: money(debt.receivable, null) })}</p>}
      <OutstandingTable invoices={debt?.outstanding ?? []} />

      <h3>{t('card.inv.subs.title')}</h3>
      <SubscriptionsTable subscriptions={subscriptions.data ?? []} />
    </div>
  )
}

/**
 * Вкладка дебиторки для карточки контрагента.
 *
 * Отдельно от вкладки «Финансы» (#70), хотя ageing есть и там: та отвечает на «можно ли с
 * ним работать» (лимит, блокировка, договоры), эта — на «сколько он должен и что мы с этим
 * делаем» (счета, подписки). Ageing в обеих читается **из одного эндпоинта**, поэтому
 * цифра одна; дублируется представление, а не источник.
 */
export function receivablesTab(): CardTab {
  return {
    id: 'receivables',
    labelKey: 'card.tab.receivables',
    render: (context: CardTabContext) => <ReceivablesPanel recordId={context.recordId} />,
  }
}

export { ReceivablesPanel, OutstandingTable, SubscriptionsTable }
